import {
  AcceptIncomingCallAction,
  ActiveCallPhase,
  CallStatus,
  CallStatusUpdate,
  CallsActionTypes,
  ClearIncomingCallAction,
  EndActiveCallAction,
  IncomingCall,
  RequestOutgoingCallAction,
  SetActiveCallCredsAction,
  SetActiveCallPhaseAction,
  SetCallStatusAction,
  SetIncomingCallAction,
  SubscribeCallStatusAction,
  UnsubscribeCallStatusAction,
} from './callsTypes';
import { StartCallResponse } from './callsApi';

export const setIncomingCall = (call: IncomingCall): SetIncomingCallAction => ({
  type: CallsActionTypes.SET_INCOMING_CALL,
  payload: call,
});

export const clearIncomingCall = (): ClearIncomingCallAction => ({
  type: CallsActionTypes.CLEAR_INCOMING_CALL,
});

export const setCallStatus = (
  update: CallStatusUpdate,
): SetCallStatusAction => ({
  type: CallsActionTypes.SET_CALL_STATUS,
  payload: update,
});

export const requestOutgoingCall = (
  peerCustomerId: number,
  peerName: string,
): RequestOutgoingCallAction => ({
  type: CallsActionTypes.REQUEST_OUTGOING_CALL,
  payload: { peerCustomerId, peerName },
});

export const acceptIncomingCall = (
  peerName: string,
): AcceptIncomingCallAction => ({
  type: CallsActionTypes.ACCEPT_INCOMING_CALL,
  payload: { peerName },
});

export const setActiveCallCreds = (
  call: StartCallResponse,
): SetActiveCallCredsAction => ({
  type: CallsActionTypes.SET_ACTIVE_CALL_CREDS,
  payload: {
    callId: call.call_id,
    chatId: call.chat_id,
    janusUrl: call.janus_url,
    roomId: call.room_id,
    token: call.token,
  },
});

export const setActiveCallPhase = (
  phase: ActiveCallPhase,
): SetActiveCallPhaseAction => ({
  type: CallsActionTypes.SET_ACTIVE_CALL_PHASE,
  payload: { phase },
});

export const endActiveCall = (): EndActiveCallAction => ({
  type: CallsActionTypes.END_ACTIVE_CALL,
});

export const subscribeCallStatus = (
  chatId: string,
): SubscribeCallStatusAction => ({
  type: CallsActionTypes.SUBSCRIBE_CALL_STATUS,
  payload: { chatId },
});

export const unsubscribeCallStatus = (
  chatId: string,
): UnsubscribeCallStatusAction => ({
  type: CallsActionTypes.UNSUBSCRIBE_CALL_STATUS,
  payload: { chatId },
});

const isCallStatus = (s: unknown): s is CallStatus =>
  s === 'RING' ||
  s === 'IN_PROGRESS' ||
  s === 'COMPLETED' ||
  s === 'REJECTED' ||
  s === 'NO_ANSWER' ||
  s === 'DISCONNECTED';

/**
 * Parse an `omcare/users/<id>/calls/incoming` MQTT payload. Returns null on a
 * malformed message rather than throwing (MQTT is untrusted input).
 */
export const parseIncomingCall = (raw: unknown): IncomingCall | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  if (m.name !== 'incoming_call') return null;
  if (
    typeof m.call_id !== 'number' ||
    typeof m.room_id !== 'number' ||
    typeof m.janus_url !== 'string' ||
    typeof m.token !== 'string'
  ) {
    return null;
  }
  return {
    callId: m.call_id,
    chatId: typeof m.chat_id === 'string' ? m.chat_id : '',
    callerCustomerId:
      typeof m.caller_customer_id === 'number' ? m.caller_customer_id : -1,
    janusUrl: m.janus_url,
    roomId: m.room_id,
    token: m.token,
  };
};

/** Parse an `omcare/bus/<chat>/calls/status` MQTT payload. Null if malformed. */
export const parseCallStatus = (raw: unknown): CallStatusUpdate | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.call_id !== 'number' || !isCallStatus(m.status)) return null;
  return {
    callId: m.call_id,
    chatId: typeof m.chat_id === 'string' ? m.chat_id : '',
    status: m.status,
  };
};
